import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  addDays,
  isWeekend,
  isMonday,
  isTuesday,
  isWednesday,
  isThursday,
  isFriday,
} from "date-fns";
import { checkAvailability } from "../../_redux/order/orderCrud";
import { LoadingSpinner } from "../../../../../_metronic/_partials/controls/LoadingSpinner";
import SnackBarTool from "../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";

/**
 * Inline date picker for container move-in, collection, move-out and return
 * dates. Picking a move-in date triggers a container availability check and
 * reports the outcome through the snack bar.
 *
 * Serves two flows, which supply different props: the new-order flow passes
 * `state`, `franchise` and `dispatchReducer`, while the existing-order flow
 * passes `check`, `orderDetail`, `keyC` and `setFranchiseContainer`.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik bag owning the four date fields.
 * @param {Object} [props.state] - New-order reducer state (container list and
 *   selection, selected site, product_array). New-order flow only.
 * @param {string|number} [props.franchise] - Franchise id used for the
 *   availability lookup. New-order flow only.
 * @param {Function} [props.dispatchReducer] - New-order reducer dispatch, used
 *   to store the returned availability under `avail`. New-order flow only.
 * @param {string} [props.check] - `"order"` when editing an existing order;
 *   omitted in the new-order flow.
 * @param {Object} [props.orderDetail] - Existing order being edited, read for
 *   franchise, container size, site and current move-in date.
 * @param {string} [props.keyC] - Which date the picker edits when
 *   `check === "order"`: `"moveIn"`, `"collection"`, `"moveOut"` or `"return"`.
 * @param {Function} [props.setFranchiseContainer] - Accepted for the
 *   existing-order flow but currently unused; its only call site is commented
 *   out below.
 * @returns {JSX.Element}
 */
export const ContainerModalCompoent = ({
  formik,
  state,
  franchise,
  dispatchReducer,
  check,
  orderDetail,
  keyC,
  setFranchiseContainer,
}) => {
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });

  /**
   * Monotonic id of the most recent availability request. Each date/site/size
   * change starts a new one, so comparing against it discards out-of-order
   * responses — an earlier "no availability" reply must not land after a later
   * successful one and wipe the units it wrote.
   */
  const availRequestRef = useRef(0);

  /**
   * The selection currently on screen, refreshed after each commit so an
   * in-flight response can be compared against it. Needed because the plan picker in
   * OrderSummary clears `avail` when the container size changes; the request
   * version alone cannot see that, so a reply for the previous size would
   * otherwise restore units that no longer match the order.
   */
  const selectionRef = useRef({ containerSize: "", siteId: "" });

  useEffect(() => {
    selectionRef.current = {
      containerSize: state?.containerListSelect || "",
      siteId: state?.siteSelect?.id || "",
    };
  }, [state?.containerListSelect, state?.siteSelect?.id]);

  function shouldDisableDate(date) {
    // Calculate the next 3 days from today
    for (let i = 1; i <= 3; i++) {
      const nextDay = addDays(new Date(), i);
      // Disable the date if it's a weekend (Saturday or Sunday)
      if (isWeekend(nextDay)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks container availability for the selected move-in date and reports the
   * result through the snack bar.
   *
   * @param {string} date - Selected date in `yyyy-MM-DD` format.
   * @returns {void}
   */
  const handleAvailability = (date) => {
    const requestId = ++availRequestRef.current;
    setSnack({
      call: false,
      type: "",
      message: "",
    });
    const data = {
      franchise_id: franchise || "",
      type:
        (state?.containerList &&
          state?.containerList.length &&
          state.containerList.find((e) => e.id === state?.containerListSelect)
            ?.type) ||
        "",
      container_size: state?.containerListSelect || "",
      site_id: state?.siteSelect?.id || "",
      collection_date: date || "",
    };
    checkAvailability(data)
      .then((res) => {
        // A newer selection has already been made — drop this response entirely,
        // including its snack, date clearing and loading flag, so the in-flight
        // request that supersedes it stays authoritative.
        if (requestId !== availRequestRef.current) {
          return;
        }

        // The size or site moved on while this was in flight (the plan picker
        // clears `avail` when it changes), so these units belong to a selection
        // the order no longer has. Writing them back would reinstate exactly the
        // size mismatch that clear exists to prevent.
        if (
          data.container_size !== selectionRef.current.containerSize ||
          data.site_id !== selectionRef.current.siteId
        ) {
          return;
        }

        // The plan row is missing whenever the franchise/site has no container
        // plan configured, so fall back to 0 and let the availability count
        // decide instead of crashing on an undefined row.
        const required = Number(
          state?.product_array?.find((e) => e.type === "plan")?.quantity || 0
        );
        const availableList = res?.data?.data || [];
        const condition = required <= availableList.length;
        if (availableList.length === 0 || !condition) {
          setSnack({
            call: true,
            type: "error",
            message:
              "No container is available on this date. Please select another date",
          });
          formik.setFieldValue("collection_date", "");
        } else {
          setSnack({
            call: true,
            type: "success",
            message: "Date Selected Successfully",
          });
        }
        // SET_DATA merges, so dispatch only the refreshed field. Spreading the
        // render-time `state` snapshot here would revert anything that changed
        // while the request was in flight (site, product_array, dates).
        dispatchReducer({
          type: "SET_DATA",
          data: { avail: availableList },
        });

        setLoading(false);
      })
      .catch(() => {
        // A newer selection superseded this request, or the size/site moved on
        // while it was in flight — discard this rejection without touching the
        // current selection's snack, date or loading flag, exactly as the success
        // path does. The authoritative request stays in control.
        if (
          requestId !== availRequestRef.current ||
          data.container_size !== selectionRef.current.containerSize ||
          data.site_id !== selectionRef.current.siteId
        ) {
          return;
        }
        setSnack({
          call: true,
          type: "error",
          message: "Unable to check container availability. Please try again.",
        });
        formik.setFieldValue("collection_date", "");
        setLoading(false);
      });
  };

  /**
   * Checks container availability for an existing order's move-in date.
   *
   * @param {string} date - Selected date in `yyyy-MM-DD` format.
   * @returns {void}
   */
  const handleOrderAvailability = (date) => {
    const data = {
      franchise_id: orderDetail?.franchise_id || "",
      type: orderDetail?.container_size.includes("Non-Ele") ? "NON" : "",
      container_size: orderDetail?.container_size || "",
      site_id:
        (orderDetail?.order_container_details &&
          orderDetail?.order_container_details.length > 0 &&
          orderDetail?.order_container_details[0]?.site_id) ||
        "",
      collection_date: date || "",
    };
    checkAvailability(data).then((res) => {
      const check = orderDetail?.pod;
      const condition = Number(check) <= (res?.data?.data?.length || 0);
      // if ((res.data.data?.length == 0) || !condition) {
      //   setSnack({
      //     call: true,
      //     type: "error",
      //     message: "No container is available on this date. Please select another date",
      //   });
      //   formik.setFieldValue("collection_date", orderDetail?.collection_date || null);
      // }
      // if (res.data.data?.length > 0 && condition) {
      setSnack({
        call: true,
        type: "success",
        message: "Date Selected Successfully",
      });
      // setFranchiseContainer(res.data.data)
      // }
      // dispatchReducer({ type: "avail", value: res.data.data });

      setLoading(false);
    })
    .catch(() => {
      setSnack({
        call: true,
        type: "error",
        message: "Unable to check container availability. Please try again.",
      });
      // The picked date was never validated, so put the order's existing date
      // back rather than leaving it in the form. Falls back to "" (not null)
      // to match how the modal seeds this field, since `selected` passes the
      // value straight to `new Date()` and null would resolve to the epoch.
      formik.setFieldValue(
        "collection_date",
        orderDetail?.collection_date || ""
      );
      setLoading(false);
    });
  };
  return (
    <div className="collection-date">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <DatePicker
          className="form-control"
          dateFormat="dd-MM-Y"
          selected={
            check === "order" && keyC === "moveIn"
              ? new Date(formik?.values?.collection_date)
              : check === "order" && keyC === "collection"
              ? formik?.values?.container_collection_date
                ? new Date(formik?.values?.container_collection_date)
                : new Date()
              : check === "order" && keyC === "moveOut"
              ? formik?.values?.return_date
                ? new Date(formik?.values?.return_date)
                : null
              : check === "order" && keyC === "return"
              ? formik?.values?.container_return_date
                ? new Date(formik?.values?.container_return_date)
                : new Date()
              : formik.values.date_type == "2" &&
                formik?.values?.collection_date
              ? new Date(formik?.values?.collection_date)
              : formik.values.date_type == "1" &&
                formik?.values?.container_collection_date
              ? new Date(formik?.values?.container_collection_date)
              : formik.values.date_type == "3" &&
                formik?.values?.container_return_date
              ? new Date(formik?.values?.container_return_date)
              : formik.values.date_type == "4" && formik?.values?.return_date
              ? new Date(formik?.values?.return_date)
              : null
          }
          onChange={(date) => {
            if (check === "order") {
              formik.setFieldValue(
                keyC === "collection"
                  ? "container_collection_date"
                  : keyC === "moveIn"
                  ? "collection_date"
                  : keyC === "moveOut"
                  ? "return_date"
                  : keyC === "return"
                  ? "container_return_date"
                  : "",
                moment(date).format("yyyy-MM-DD")
              );
              if (keyC === "moveIn") {
                setLoading(true);

                handleOrderAvailability(moment(date).format("yyyy-MM-DD"));
              }
              return;
            } else {
              formik.setFieldValue(
                formik.values.date_type == "1"
                  ? "container_collection_date"
                  : formik.values.date_type == "2"
                  ? "collection_date"
                  : formik.values.date_type == "3"
                  ? "container_return_date"
                  : formik.values.date_type == "4"
                  ? "return_date"
                  : "",
                moment(date).format("yyyy-MM-DD")
              );
              if (formik.values.date_type == "2") {
                setLoading(true);
                handleAvailability(moment(date).format("yyyy-MM-DD"));
              }
            }
          }}
          placeholderText="Select Collection Date"
          inline
          minDate={
            keyC === "moveOut"
              ? new Date(new Date().setDate(new Date().getDate() + 2)) // 2 weeks baad
              : new Date() // default: aaj se
          }
          shouldDisableDate={shouldDisableDate}
        />
      )}
      {snack.call ? <SnackBarTool {...snack} /> : ""}
    </div>
  );
};
